import { CheckCircle2, Clock, Phone, ShieldCheck, Star, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../api/client'
import { connectionsApi, guardianApi } from '../api/endpoints'
import { NEED_CATEGORY_LABELS, summarizeJson } from '../utils/taxonomy'
import { ErrorMessage } from './ErrorMessage'
import type {
  ChildActivityConnection,
  ContactRevealResponse,
  ChildActivityEvent,
  ChildActivityResponse,
  ConnectionStatus,
} from '../types/api'

/** How long back the event trail reaches. Connections are never windowed. */
/**
 * The "What happened" event list under a child's connections, hidden for now - the connections
 * themselves already say who asked, who approved and who accepted. Flip back to true to restore.
 */
const SHOW_EVENT_TRAIL = false
const WINDOWS = [30, 60, 90, 365]

/**
 * Plain words for each status, from a parent's point of view.
 *
 * <p>"PENDING" and "AWAITING_GUARDIAN" are the server's words for two very different situations,
 * and a parent needs to be able to tell them apart at a glance: one is waiting on a stranger, the
 * other is waiting on them.
 */
const STATUS: Record<ConnectionStatus, { label: string; className: string }> = {
  ACCEPTED: { label: 'Connected', className: 'bg-emerald-100 text-emerald-800' },
  PENDING: { label: 'Waiting for them', className: 'bg-sky-100 text-sky-800' },
  AWAITING_GUARDIAN: { label: 'Waiting for you', className: 'bg-sun-400/30 text-amber-900' },
  REJECTED: { label: 'Declined', className: 'bg-ink-100 text-ink-600' },
  CANCELLED: { label: 'Cancelled', className: 'bg-ink-100 text-ink-600' },
  EXPIRED: { label: 'Expired', className: 'bg-ink-100 text-ink-600' },
}

/** One readable sentence per event type - the log's own names mean nothing to a parent. */
const EVENT_WORDS: Record<string, (other: string) => string> = {
  connection_requested: (o) => `Asked to connect with ${o}`,
  connection_accepted: (o) => `${o} accepted the connection`,
  connection_declined: (o) => `${o} declined the connection`,
  contact_revealed: (o) => `Contact details shared with ${o}`,
  guardian_approved_connection: (o) => `You answered a request, on their behalf (${o})`,
  guardian_link_created: () => 'A parent link was requested',
  guardian_link_activated: () => 'A parent link became active',
  guardian_link_revoked: () => 'A parent link was removed',
  voice_intent: () => 'Used voice search',
}

function describe(e: ChildActivityEvent): string {
  const other = e.otherName ?? 'someone'
  const phrase = EVENT_WORDS[e.type]
  // An unknown type is shown rather than hidden: a trail that quietly drops rows is not a trail.
  return phrase ? phrase(other) : e.type.replace(/_/g, ' ')
}

function when(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function ChildActivityTrail({
  childId,
  childName,
  onClose,
}: {
  childId: string
  childName: string
  onClose: () => void
}) {
  const [data, setData] = useState<ChildActivityResponse | null>(null)
  const [days, setDays] = useState(60)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    guardianApi
      .childActivity(childId, days)
      .then((res) => {
        if (live) setData(res)
      })
      .catch((err) => {
        if (live) setError(apiErrorMessage(err, `Could not load ${childName}'s activity`))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    // Guards against a slow first response landing after the window changed and overwriting
    // the newer one.
    return () => {
      live = false
    }
  }, [childId, childName, days])

  const live = data?.connections.filter((c) => c.status === 'ACCEPTED') ?? []
  const other = data?.connections.filter((c) => c.status !== 'ACCEPTED') ?? []

  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-words font-bold text-ink-900">{childName}&apos;s connections</h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Everyone they are connected to, and how each one came about.
          </p>
        </div>
        <button onClick={onClose} aria-label="Close" className="shrink-0 text-ink-400 hover:text-ink-700">
          <X size={17} />
        </button>
      </div>

      <div className="mt-3">
        <ErrorMessage message={error} />
      </div>

      {loading && !data ? (
        <p className="mt-3 text-sm text-ink-500">Loading...</p>
      ) : !data ? null : (
        <>
          <section className="mt-4">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
              Connected now ({live.length})
            </h3>
            {live.length === 0 ? (
              <p className="mt-2 text-sm text-ink-500">
                Not connected to anyone yet. Nobody can reach {childName} until you approve it.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {live.map((c) => (
                  <ConnectionRow key={c.requestId} connection={c} />
                ))}
              </div>
            )}
          </section>

          {other.length > 0 && (
            <section className="mt-5">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
                Requests and past decisions ({other.length})
              </h3>
              <div className="mt-2 space-y-2">
                {other.map((c) => (
                  <ConnectionRow key={c.requestId} connection={c} />
                ))}
              </div>
            </section>
          )}

          {SHOW_EVENT_TRAIL && (
          <section className="mt-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
                What happened
              </h3>
              <div className="flex items-center gap-1">
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    onClick={() => setDays(w)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                      days === w ? 'bg-ink-900 text-ink-50' : 'text-ink-500 hover:bg-ink-100'
                    }`}
                  >
                    {w === 365 ? '1 year' : `${w} days`}
                  </button>
                ))}
              </div>
            </div>

            {data.events.length === 0 ? (
              <p className="mt-2 text-sm text-ink-500">
                Nothing recorded in this period. The connections above are still shown in full -
                only this list is limited to the last {data.windowDays} days.
              </p>
            ) : (
              <ol className="mt-2 space-y-2">
                {data.events.map((e) => (
                  <li key={e.id} className="flex gap-2.5 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ink-300" />
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-800">{describe(e)}</span>
                      <span className="ml-2 whitespace-nowrap text-xs text-ink-400">{when(e.at)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
          )}
        </>
      )}
    </div>
  )
}

function ConnectionRow({ connection: c }: { connection: ChildActivityConnection }) {
  const status = STATUS[c.status] ?? { label: c.status, className: 'bg-ink-100 text-ink-600' }
  const [contact, setContact] = useState<ContactRevealResponse | null>(null)
  const [revealError, setRevealError] = useState<string | null>(null)

  async function reveal() {
    setRevealError(null)
    try {
      setContact(await connectionsApi.getContact(c.requestId))
    } catch (err) {
      setRevealError(apiErrorMessage(err, 'Could not show these details'))
    }
  }

  return (
    <div className="rounded-xl border border-ink-200 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="break-words font-semibold text-ink-900">
          {contact?.fullName ?? c.other.name}
        </span>
        <span className={`pill ${status.className}`}>{status.label}</span>
        {c.other.verified && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            <ShieldCheck size={13} /> Verified
          </span>
        )}
        {c.other.averageRating !== null && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-500">
            <Star size={13} className="fill-sun-400 text-sun-400" />
            {c.other.averageRating.toFixed(1)} ({c.other.ratingCount})
          </span>
        )}
      </div>

      <p className="mt-1 text-xs text-ink-500">
        {c.direction === 'OUTGOING' ? 'Your child asked' : 'They asked your child'}
        {c.aboutCategory ? ` · about ${c.aboutCategory.replace(/_/g, ' ').toLowerCase()}` : ''}
        {c.sentAt ? ` · ${when(c.sentAt)}` : ''}
      </p>

      {/* The parent's own decision, written back to them. The whole point of approving a
          connection is being able to see later that you did. */}
      {c.guardianApprovedAt && (
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-600">
          <CheckCircle2 size={13} className="text-emerald-600" />
          Approved by {c.guardianApprovedByName ?? 'a parent'} on {when(c.guardianApprovedAt)}
        </p>
      )}
      {c.heldForGuardian && (
        <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-800">
          <Clock size={13} /> Waiting for your answer
        </p>
      )}

      {/* What they offer. Shown whatever the status, because it is not PII and it is the whole
          substance of the decision the parent made or is about to make. */}
      {c.other.services.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {c.other.services.slice(0, 3).map((s, i) => (
            <li key={`${s.category}-${i}`} className="text-xs text-ink-600">
              <span className="font-semibold text-ink-800">
                {NEED_CATEGORY_LABELS[s.category] ?? s.category}
              </span>
              {summarizeJson(s.items) ? (
                <span className="text-ink-500"> · {summarizeJson(s.items)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* A parent is the third party whose approval this connection needed, so they may read the
          details their child now has - and like every other reveal, the read is audited. */}
      {c.contactAvailable && !contact && (
        <button onClick={reveal} className="btn-ghost mt-2 !px-2.5 !py-1 text-xs gap-1.5">
          <Phone size={12} /> Show contact details
        </button>
      )}
      {contact && (
        <div className="mt-2 rounded-lg bg-ink-50 px-2.5 py-2 text-xs">
          <div className="break-words">
            <span className="text-ink-500">Mobile</span>{' '}
            <span className="font-semibold text-ink-900">{contact.phone}</span>
          </div>
          <div className="break-words">
            <span className="text-ink-500">Email</span>{' '}
            <span className="font-semibold text-ink-900">{contact.email}</span>
          </div>
          <p className="mt-1 text-ink-400">This reveal is recorded, as every reveal is.</p>
        </div>
      )}
      <ErrorMessage message={revealError} />
    </div>
  )
}
